import { z } from 'zod';

/**
 * 서비스 롤업 (26회차).
 *
 * 27회차의 첫 화면이 "서비스 목록"이 되면서 필요해진 계약이다(2026-09-09 사용자 결정 2).
 * 앱마다 한 줄로 "지금 괜찮은가"를 답한다. 값은 전부 **이미 서버에 있는 것**을 모은 것이고
 * 새로 재는 것은 없다 — 인스턴스 생존, 끝난 요청 5분 창, 발화 중인 알림, 저장 폴더 용량이다.
 *
 * 목록은 셋의 합집합이다.
 *   1. 실시간·트레이스 신호가 오는 서비스 (`InstancePresence`)
 *   2. 지금 발화 중인 알림이 가리키는 서비스
 *   3. 저장 폴더가 있는 서비스 — 조용해진 지 오래여도 자료는 남아 있다
 *
 * 3번만 있는 서비스는 `lastSeenAt`이 없고, 폴더 이름에서 원래 이름을 되돌릴 수 없으면
 * `nameFromFolder`가 참이다(그 값은 조회 조건으로 그대로 쓸 수 없다).
 */

const count = z.number().int().nonnegative();
const nonneg = z.number().nonnegative();
const bytes = z.number().int().nonnegative();
const epochMs = z.number().int().nonnegative();

/**
 * 서비스 한 줄의 건강. **활성 알림·생존·최근 5분 오류율 셋 중 가장 나쁜 쪽**이다
 * (리뷰 R26-11, 오류율은 34회차).
 *
 *   생존만 보면 — **1초 스냅샷을 보내는 인스턴스가** 전부 조용해졌으면 `critical`(지금 아무
 *   요청도 못 받는다), 일부만 조용하면 `warning`(남은 인스턴스가 받고 있다). 트레이스만
 *   인스턴스가 조용한 것은 요청이 없었다는 뜻이라 보지 않는다. 인스턴스가 0이면 `ok`
 *   (저장만 남은 서비스는 아픈 것이 아니다).
 *   오류율은 5분 요청이 20건 이상일 때만 본다 — 5% 이상 `warning`, 20% 이상 `critical`.
 */
export const ServiceHealth = z.enum(['ok', 'warning', 'critical']);
export type ServiceHealth = z.infer<typeof ServiceHealth>;

export const ServiceSummary = z.object({
  /** OTel service.name. `nameFromFolder`가 참이면 폴더 접두사라 원래 이름이 아닐 수 있다 */
  service: z.string().min(1),
  /**
   * 이름을 저장 폴더에서 되짚었다 (26회차). 폴더 이름은 소문자 canonical의 접두사 + 해시라
   * 되돌릴 수 없다. 참이면 `?service=` 조건으로 그대로 쓰면 안 맞을 수 있다
   */
  nameFromFolder: z.boolean().default(false),
  instances: z.object({
    total: count,
    /** 그중 신호가 끊긴 수. 보고 방식을 가리지 않은 합이다 */
    stale: count,
    /**
     * 보고 방식으로 나눈 수 (34회차 리뷰 반영). 한 서비스에 우리 에이전트가 붙은 프로세스와
     * 표준 OTel로 붙은 프로세스가 섞일 수 있는데, 그 둘의 "조용함"은 뜻이 다르다 —
     * 실시간 인스턴스가 조용한 것은 신호가 끊긴 것이고 트레이스만 인스턴스가 조용한 것은
     * 요청이 없었다는 뜻이다. 화면이 그것을 다른 말로 적을 수 있게 나눠 준다.
     * 34회차 전 서버의 응답에는 없다
     */
    realtime: count.optional(),
    traceOnly: count.optional(),
    /** `stale` 중 실시간 인스턴스 수. 이쪽만 "끊김"이다 */
    staleRealtime: count.optional(),
    /** `stale` 중 트레이스만 인스턴스 수. 이쪽은 "조용함"이다 */
    staleTraceOnly: count.optional(),
  }),
  /** 1초 스냅샷이 오는 인스턴스가 하나라도 있는가. 거짓이면 "트레이스만" 서비스다 */
  realtime: z.boolean(),
  /**
   * 마지막 신호 시각. 아무 기록도 없으면 없다.
   *
   * 관제 서버가 다시 뜨면 생존 판정의 기억이 비므로, 그때는 **저장에 남은 마지막 기록**
   * (요청 파일이 쓰인 시각)으로 답하고 `lastSeenFromStorage`를 세운다 (34회차 리뷰 반영)
   */
  lastSeenAt: epochMs.optional(),
  /**
   * `lastSeenAt`이 저장 파일에서 온 값인가 (34회차 리뷰 반영). 참이면 그 뒤로 이 서버가
   * 받은 요청은 없다는 뜻이고, 값 자체도 파일 회전 주기(기본 60초)만큼 늦을 수 있다.
   * 34회차 전 서버의 응답에는 없다
   */
  lastSeenFromStorage: z.boolean().optional(),
  /** 최근 5분 요약. 끝난 요청 피드에서 센다(sse 제외). 트레이스만 서비스도 채워진다 */
  last5m: z.object({
    requests: count,
    /** `오류 건수 / 요청 수`. 0~1. 요청이 없으면 0 */
    errorRate: z.number().min(0).max(1),
    p95Ms: nonneg,
  }),
  /** 지금 발화 중인 알림 중 이 서비스 것 */
  activeAlerts: count,
  storage: z.object({ hotBytes: bytes, coldBytes: bytes }),
  health: ServiceHealth,
});
export type ServiceSummary = z.infer<typeof ServiceSummary>;

/**
 * `GET /api/services` 응답. 밖으로 내놓는 `GET /api/external/services`는 같은 줄을
 * `services` 칸에 싣고 `version`을 얹는다(`ExternalServices`)
 */
export const ServicesResult = z.object({
  at: epochMs,
  /** `last5m`을 센 창의 길이(ms) */
  windowMs: count,
  /** 이름순 */
  rows: z.array(ServiceSummary),
});
export type ServicesResult = z.infer<typeof ServicesResult>;
